import { assertNotEmpty, dateUtils } from '@festina-life/core'
import { Tooltip } from '@festina-life/ui/feedback'

import { useUntilChangesetKeyValue } from '../../context/useUntilChangesetKeyValue'
import { getParameterDefinitionInOut } from '../../uiModels/parameterOperations/helpers'
import type { IndexedParameterDefinitionCreateOperationWithMetaData } from '../../uiModels/parameterOperations/types'
import { NO_DATE_SYMBOL } from '../../util/constants'
import { MutipleVersionCircles } from '../versionedTimeline/MutipleVersionCircles'
import type { useTimelineScale } from '../versionedTimeline/useTimelineScale'
import { ParameterDefinitionInOutTooltipContent } from './ParameterDefinitionInOutTooltipContent'
import { useIsParameterVersionActive } from './useIsParameterVersionActive'

type Props = {
  dateToPixel: ReturnType<typeof useTimelineScale>['dateToPixel']
  versions: IndexedParameterDefinitionCreateOperationWithMetaData[]
  earliestVersionDate: Date
}

export const MultipleParameterDefinitionVersionsCircle = ({
  dateToPixel,
  versions,
  earliestVersionDate,
}: Props) => {
  const untilChangesetKeyValue = useUntilChangesetKeyValue()
  const isAnyVersionLockedToChangeset =
    !!untilChangesetKeyValue &&
    versions.some(
      (version) =>
        untilChangesetKeyValue === version.changesetHistory?.changesetKey,
    )
  const isVersionActive = useIsParameterVersionActive()
  const isAnyVersionActive = versions.some(isVersionActive)
  const firstVersion = versions[0]
  assertNotEmpty(firstVersion)
  const singleVersionFromDate = getParameterDefinitionInOut(
    versions.at(0)!,
  ).fromDate
  const leftPosition = dateToPixel(
    singleVersionFromDate
      ? dateUtils.dateUtcAsLocal(singleVersionFromDate).toDate()
      : NO_DATE_SYMBOL,
  )

  return (
    <Tooltip
      tip={
        <ParameterDefinitionInOutTooltipContent
          versions={versions}
          versionId={versions.at(0)?.internalOperation.entity?.name ?? '-'}
          earliestVersionDate={earliestVersionDate}
        />
      }
      isInteractive
    >
      <g>
        <MutipleVersionCircles
          count={versions.length}
          left={leftPosition}
          isAnyVersionLockedToChangeset={isAnyVersionLockedToChangeset}
          isAnyVersionActive={isAnyVersionActive}
        />
      </g>
    </Tooltip>
  )
}
